// router-runtime.ts — Routeur runtime (hashchange) du frontend.
//
// Tranche extraite de `core.ts` (campagne strangler god-modules, août 2026) :
// le routeur est un bloc autonome — enregistrement des routes, résolution du
// hash, garde de permission, dispatch. Il ne partageait avec le reste de
// `core.ts` que des helpers déjà exposés en globals (`esc`, `t`, `safeToast`,
// `setContent`, `setHeader`, `pages`), qu'il consomme ici en bare-refs comme
// les autres modules extraits.
//
// La couche TYPÉE d'enregistrement (`defineRoute`, `navigateTo`, patterns) vit
// dans `../router.ts` ; ici c'est le moteur runtime qu'elle pilote.
//
// `core.ts` réexpose `router` / `registerPageDisposer` / `updateNav` sur
// `window` (shim historique) — les pages continuent donc à les appeler en
// bare-ref, sans changement.

import type { PageHandler, PagesGlobal } from './core.js';

interface RouteEntry {
  re: RegExp;
  keys: string[];
  handler: PageHandler;
  perm: string | null;
}

interface RouteMatch {
  handler: PageHandler;
  params: Record<string, string>;
  perm: string | null;
}

interface OrnexiaRouter {
  routes: RouteEntry[];
  _disposers: Array<() => void>;
  // Génération de navigation. Incrémentée à CHAQUE dispatch : elle permet à un
  // handler qui se termine de savoir s'il est encore la page courante.
  _gen: number;
  // Générations encore en vol. Le correctif n'est déclenché que par le DERNIER
  // handler à rendre la main, sinon deux handlers périmés se relanceraient
  // mutuellement à l'infini.
  _inflight: Set<number>;
  // Un seul rattrapage à la fois.
  _fixScheduled: boolean;
  on(pattern: string, handler: PageHandler, perm?: string | null): void;
  resolve(hash: string): RouteMatch | null;
  navigate(hash: string): void;
  _checkPerm(perm: string | null): boolean;
  _isMobileBlockedRoute(hash: string): boolean;
  _runPage(match: RouteMatch, gen: number): void;
  dispatch(): void;
  init(): void;
}

const router: OrnexiaRouter = {
  routes: [],
  _disposers: [],
  _gen: 0,
  _inflight: new Set<number>(),
  _fixScheduled: false,
  // `perm` : code (ou liste OR séparée d'espaces) requis pour entrer sur la
  // page. Si absent → aucune vérif. Si l'user n'a pas la perm, on redirige
  // vers #dashboard avec un toast.
  on(pattern, handler, perm) {
    const keys: string[] = [];
    const re = new RegExp(
      '^' +
        pattern.replace(/:([^/]+)/g, (_m, k: string) => {
          keys.push(k);
          return '([^/]+)';
        }) +
        '$',
    );
    this.routes.push({ re, keys, handler, perm: perm || null });
  },
  resolve(hash) {
    // Strip leading "#/" PUIS query string : `#actions?all=1` doit matcher
    // la route `actions`. Sans ce strip, F5 sur une page avec query string
    // (filtres actions persistes en URL) tombait en fallback dashboard
    // (= page Projets), perte du contexte.
    const raw = (hash || '').replace(/^#\/?/, '') || 'actions';
    const path = raw.split('?')[0] || 'actions';
    for (const r of this.routes) {
      const m = path.match(r.re);
      if (m) {
        const params: Record<string, string> = {};
        r.keys.forEach((k, i) => {
          params[k] = m[i + 1] ?? '';
        });
        return { handler: r.handler, params, perm: r.perm };
      }
    }
    return null;
  },
  navigate(hash) {
    window.location.hash = hash;
  },
  _checkPerm(perm) {
    if (!perm) return true;
    if (typeof window.hasAnyPermission !== 'function') return true; // helpers pas encore chargés
    // Race au F5 : si __authUser n'est pas encore là, on laisse passer.
    // Le backend rejettera de toute façon si la perm manque vraiment.
    // Sans ce check, F5 sur une page admin donnait "Accès refusé" + redirect
    // dashboard alors que l'user a bien la perm — il fallait quitter et
    // revenir pour que ça remarche (cf. bug B4 avril 2026).
    const authUser = window.__authUser as { permissions?: string[] } | null | undefined;
    if (!authUser || !Array.isArray(authUser.permissions)) {
      return true;
    }
    const codes = perm.split(/\s+/);
    if (window.hasAnyPermission(codes)) return true;
    safeToast(t('common.accessDeniedToast'), 'error');
    // Redirige vers dashboard s'il y a la perm, sinon on reste là (évite la boucle).
    const fallback =
      window.hasPermission && window.hasPermission('projects.read') ? '#dashboard' : null;
    if (fallback && window.location.hash !== fallback) {
      setTimeout(() => {
        window.location.hash = fallback;
      }, 10);
    } else {
      setContent(`<div class="empty-state" style="padding:40px;text-align:center;">
        <h2>${esc(t('common.accessDenied'))}</h2>
        <p>${esc(t('common.accessDeniedDetail'))}</p>
      </div>`);
      setHeader(t('common.accessDenied'), '');
    }
    return false;
  },
  _isMobileBlockedRoute(hash) {
    // En mobile, les pages admin sont inaccessibles (option A). On les
    // bloque AUSSI côté router pour attraper les accès directs par URL.
    if (!(window.isMobile && window.isMobile())) return false;
    const blocked = window.MOBILE_HIDDEN_ROUTES;
    if (!blocked) return false;
    return Array.from(blocked).some((h) => hash === h || hash.startsWith(h + '/'));
  },
  /** Lance le handler de page, et RATTRAPE s'il rend trop tard.
   *
   * Les handlers sont asynchrones et écrivent dans `#content` quand ils
   * aboutissent, pas quand ils partent. Une page lente (`#actions` et son
   * chargement de listes) qui se termine APRÈS que l'utilisateur a cliqué sur
   * un autre menu écrase donc le contenu de la nouvelle page : l'écran affiche
   * `#actions` pendant que la navigation, elle, montre l'autre menu comme
   * sélectionné. L'utilisateur est alors coincé — le hash vaut déjà celui de
   * l'autre menu, donc recliquer dessus n'émet aucun `hashchange` et ne
   * redéclenche rien.
   *
   * On ne peut pas annuler l'écriture d'un handler déjà parti (aucun contexte
   * asynchrone ne permet de savoir, dans `setContent`, quel handler écrit). On
   * corrige donc APRÈS : dès que le dernier handler en vol rend la main, si sa
   * génération n'est plus la courante, on redispatche la route courante. Le
   * rendu final correspond toujours au hash final.
   *
   * `_inflight` évite la boucle : sans lui, deux handlers périmés qui se
   * terminent l'un après l'autre déclencheraient chacun un dispatch, dont les
   * handlers deviendraient périmés à leur tour.
   */
  _runPage(match, gen) {
    this._inflight.add(gen);
    void Promise.resolve()
      .then(() => match.handler(match.params))
      .catch(() => {
        /* la page a déjà affiché son erreur ; ne pas casser le rattrapage */
      })
      .finally(() => {
        this._inflight.delete(gen);
        // Je suis la page courante : rien à rattraper, mon rendu fait foi.
        if (gen === this._gen) return;
        // Un rattrapage est déjà programmé : deux handlers périmés qui se
        // terminent dans le même tour n'en déclenchent qu'un.
        if (this._fixScheduled) return;
        this._fixScheduled = true;
        // Différé d'un tour : le handler de la génération COURANTE peut très
        // bien se terminer après moi (c'est même le cas nominal — il est plus
        // rapide, donc il a démarré son écriture avant). S'il est encore en
        // vol, c'est lui qui rendra en dernier et il n'y a rien à corriger.
        queueMicrotask(() => {
          this._fixScheduled = false;
          if (this._inflight.size === 0) this.dispatch();
        });
      });
  },
  dispatch() {
    // Cleanup page précédent : exécute tous les disposers enregistrés
    // (cf. registerPageDisposer) pour retirer les listeners attachés à
    // document/window. Sans ça, à chaque navigation un nouveau listener
    // global s'empile (leak audit P1 #9 — Cmd+K admin/vocabulary,
    // closeOnOutside dropdown actions, ESC global, etc.).
    if (this._disposers.length > 0) {
      this._disposers.forEach((d) => {
        try {
          d();
        } catch {
          /* best-effort */
        }
      });
      this._disposers.length = 0;
    }
    // Page d'accueil (hash vide) : #actions sur desktop, mais #postits sur
    // mobile — la saisie rapide de post-its est l'usage mobile principal, et
    // renderPostitsPage met le focus sur le champ de texte (#pi-desc) au rendu.
    const hash =
      window.location.hash || (window.isMobile && window.isMobile() ? '#postits' : '#actions');
    const match = this.resolve(hash);
    // Incrémentée AVANT de lancer quoi que ce soit : tout handler encore en vol
    // devient périmé à cet instant précis.
    const gen = ++this._gen;
    updateNav(hash);

    // Flag body : page postit OU liste des actions (pour garder
    // l'edition en mobile). Avril 2026 : extension du mode mobile
    // editable a la page #actions (liste des activites) en plus de
    // #postits. Toutes les autres pages restent en mode lecture seule
    // sur mobile.
    const isPostitPage = hash === '#postits' || hash.startsWith('#postits/');
    // « Actions » éditables en mobile : la LISTE (`#actions`) ET la page de
    // MAJ d'une action (`#action/:id`, ouverte au clic sur l'ID/description).
    // Sans `#action/`, la page d'édition tombait en mobile-readonly → CTA
    // Enregistrer masqué et champs gelés (bug mobile mai 2026).
    const isActionsPage =
      hash === '#actions' || hash.startsWith('#actions') || hash.startsWith('#action/');
    // Page réunion ÉDITABLE en mobile : le détail d'une réunion
    // (`#meeting/:id`, `#meeting/new`) et l'assistant de préparation
    // (`#meeting-support`). On exclut volontairement la LISTE
    // `#meetings-records` (consultation seule). Sans cette exemption, le
    // mode mobile-readonly masquait les CTA primaires (Enregistrer,
    // « Lancer l'assistant », Enregistrer note) — bug mobile mai 2026.
    const isMeetingPage = hash.startsWith('#meeting/') || hash.startsWith('#meeting-support');
    document.body.classList.toggle('is-postit-page', isPostitPage);
    document.body.classList.toggle('is-actions-page', isActionsPage);
    document.body.classList.toggle('is-meeting-page', isMeetingPage);
    const mobileReadOnly =
      !!(window.isMobile && window.isMobile()) && !isPostitPage && !isActionsPage && !isMeetingPage;
    document.body.classList.toggle('mobile-readonly', mobileReadOnly);

    // Mobile : bloque les routes admin (option A).
    if (this._isMobileBlockedRoute(hash)) {
      safeToast(t('common.mobileBlocked'), 'error');
      setTimeout(() => {
        window.location.hash = '#dashboard';
      }, 10);
      return;
    }

    if (match) {
      if (!this._checkPerm(match.perm)) return;
      this._runPage(match, gen);
    } else {
      // Fallback : page inconnue → dashboard (sous réserve de perm).
      if (this._checkPerm('projects.read')) {
        const pagesGlobal = (typeof pages !== 'undefined' ? pages : null) as PagesGlobal | null;
        if (pagesGlobal?.dashboard) void pagesGlobal.dashboard({});
      }
    }
  },
  init() {
    this._disposers = [];
    this._gen = 0;
    this._inflight = new Set<number>();
    this._fixScheduled = false;
    window.addEventListener('hashchange', () => this.dispatch());
    // Premier rendu APRÈS le chargement des permissions (bug août 2026) :
    // `_checkPerm` laisse volontairement passer quand `__authUser` n'est pas
    // encore là (course au F5, cf. bug B4). Or `auth_ui.bootstrap()` est async :
    // au rechargement qui suit une connexion, le router pouvait dispatcher
    // AVANT que /me réponde → la page s'affichait sans garde, et rien ne
    // repassait dessus ensuite (`auth:ready` ne fait que le masquage du menu).
    // Se reconnecter avec un compte sans `users.read` sur `#admin/users`
    // laissait donc la page ouverte, sans « Accès refusé ». On attend donc que
    // les permissions soient connues pour dispatcher la première fois.
    const authUser = window.__authUser as { permissions?: unknown } | null | undefined;
    if (authUser && Array.isArray(authUser.permissions)) {
      this.dispatch();
    } else {
      // Pas d'auth → `auth_ui` affiche l'overlay de connexion et la connexion
      // recharge la page : ne rien rendre derrière l'overlay est correct.
      window.addEventListener('auth:ready', () => this.dispatch(), { once: true });
    }
  },
};

// registerPageDisposer : à appeler depuis une page après avoir attaché
// un listener à document / window (les nœuds DOM internes au #content
// sont GC à chaque setContent, pas besoin de remove). La callback est
// invoquée par le router au prochain dispatch — utile pour shortcuts
// (Cmd+K), closeOnOutside dropdown, ESC modale, etc.
function registerPageDisposer(fn: () => void): void {
  router._disposers.push(fn);
}

function updateNav(hash: string): void {
  document.querySelectorAll<HTMLAnchorElement>('#sidebar nav a[href]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    a.classList.toggle('active', hash === href || (href !== '#dashboard' && hash.startsWith(href)));
  });
}

export { router, registerPageDisposer, updateNav };
